import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import SftGlobalData from '../library/globalData';
import './DocumentScreen.css';

function DocumentScreen({ isLoggedIn, language, docIdOverride }) {
    const { docId: docIdFromParams } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [document, setDocument] = useState(null);
    const [htmlData, setHtmlData] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Use override if provided (for single doc mode), otherwise use from URL params
    const docId = docIdOverride || docIdFromParams;

    console.log('DocumentScreen render:', { docId, docIdOverride, docIdFromParams, isLoggedIn, language });

    // Process HTML to add link targets and image click handlers
    const processHtml = (html) => {
        const tempDiv = window.document.createElement('div');
        tempDiv.innerHTML = html;

        // Add target and rel to all <a> tags that don't already have them
        const allLinks = tempDiv.querySelectorAll('a');
        allLinks.forEach(link => {
            if (!link.hasAttribute('target')) {
                link.setAttribute('target', '_blank');
            }
            if (!link.hasAttribute('rel')) {
                link.setAttribute('rel', 'noopener noreferrer');
            }
        });

        // Add onclick to images that are NOT inside an <a> tag
        const allImages = tempDiv.querySelectorAll('img');
        allImages.forEach(img => {
            // Check if this image has an <a> tag as ancestor
            let hasLinkParent = false;
            let parent = img.parentElement;

            while (parent && parent !== tempDiv) {
                if (parent.tagName === 'A') {
                    hasLinkParent = true;
                    break;
                }
                parent = parent.parentElement;
            }

            // Only add onclick if the image is not inside an <a> tag
            if (!hasLinkParent && !img.hasAttribute('onclick')) {
                img.setAttribute('onclick', 'this.requestFullscreen();');
                img.style.cursor = 'pointer';
            }
        });

        return tempDiv.innerHTML;
    };

    useEffect(() => {
        // Always fetch document if we have a docId (for single doc mode via /red/ route)
        if (docId) {
            fetchDocument();
        }
    }, [docId, language]);

    const fetchDocument = async () => {
        setLoading(true);
        setError(null);

        const response = await SftGlobalData.post('docs&action=fetchManual', {
            language: language,
            id: docId
        });

        if (response.success && response.data?.detail?.[0]) {
            const docData = response.data.detail[0];
            if (docData.approved_by > 0) {
                setDocument(docData);
                setHtmlData(processHtml(docData.html));
            } else {
                setError(t('noDocument'));
            }
        } else {
            setError(response.message || t('noDocument'));
        }

        setLoading(false);
    };

    if (loading) {
        return (
            <div className="document-screen loading">
                <ThreeDots color="#0071BB" height={60} width={60} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="document-screen error">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="document-screen">
            {document && htmlData && (
                <div
                    className="document-content"
                    dangerouslySetInnerHTML={{ __html: htmlData }}
                />
            )}
        </div>
    );
}

export default DocumentScreen;
